import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../hooks/api";
import { API_URLS } from "../App";
import ManageOffer from "../components/Offers/ManageOffer";
import ListOfferItems from "../components/Offers/ListOfferItems";
import { useLayoutContext } from "../context/LayoutContext";
import OfferProposal from "../model/OfferProposal";

interface OffersFilterParams {
  search: string;
  reseller_id?: string;
  commercial_id?: string;
  page: number;
  per_page: number;
  sort_by: string;
  sort_direction: "asc" | "desc";
  begin_date?: string;
  end_date?: string;
  status?: string;
  type?: string;
}

const OfferPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    //loading,
    //setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
  } = useLayoutContext();

  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [offerId, setOfferId] = useState<string | undefined>(id);
  const [offers, setOffers] = useState<OfferProposal[]>([]);
  const [filters, setFilters] = useState<OffersFilterParams>({
    search: window.location.search.includes("search=")
      ? new URLSearchParams(window.location.search).get("search") || ""
      : "",
    reseller_id: window.location.search.includes("reseller_id=")
      ? new URLSearchParams(window.location.search).get("reseller_id") ||
        undefined
      : undefined,
    page: window.location.search.includes("page=")
      ? parseInt(
          new URLSearchParams(window.location.search).get("page") || "1",
          10,
        )
      : 1,
    per_page: window.location.search.includes("per_page=")
      ? parseInt(
          new URLSearchParams(window.location.search).get("per_page") || "10",
        )
      : 10,
    sort_by: window.location.search.includes("sort_by=")
      ? new URLSearchParams(window.location.search).get("sort_by") ||
        "created_at"
      : "created_at",
    sort_direction: window.location.search.includes("sort_direction=")
      ? (new URLSearchParams(window.location.search).get("sort_direction") ||
          "desc") === "asc"
        ? "asc"
        : "desc"
      : "desc",
    begin_date: window.location.search.includes("begin_date=")
      ? new URLSearchParams(window.location.search).get("begin_date") ||
        undefined
      : undefined,
    end_date: window.location.search.includes("end_date=")
      ? new URLSearchParams(window.location.search).get("end_date") || undefined
      : undefined,
    status: window.location.search.includes("status=")
      ? new URLSearchParams(window.location.search).get("status") || undefined
      : undefined,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [resellersList, setResellersList] = React.useState<ResellerSummary[]>(
    [],
  );
  const [commercialsList, setCommercialsList] = React.useState<
    ResellerSummary[]
  >([]);

  const fetchOffer = async (offerId: string) => {
    setLoading(true);
    try {
      const response = await api.get(API_URLS.OFFERS + "/" + offerId);
      handleManageOffer(response.data);
    } catch (error: any) {
      setSystemError(error.message || "Erro ao buscar proposta.");
      console.warn("Error fetching offer:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOffers = async () => {
    const loading = () => {
      if (!id) {
        setLoading(true);
        return () => {
          setTimeout(() => setLoading(false), 500);
        };
      }
      setOffers([]);
      return () => {};
    };
    const closeLoading = loading();
    try {
      const response = await api.get(
        API_URLS.OFFERS +
          `/?page=${filters.page}&per_page=${filters.per_page}` +
          `&sort_by=${filters.sort_by}&sort_direction=${filters.sort_direction}` +
          (filters.search
            ? `&search=${encodeURIComponent(filters.search)}`
            : "") +
          (filters.commercial_id
            ? `&commercial_id=${filters.commercial_id}`
            : "") +
          (filters.reseller_id ? `&reseller_id=${filters.reseller_id}` : "") +
          (filters.begin_date ? `&begin_date=${filters.begin_date}` : "") +
          (filters.end_date ? `&end_date=${filters.end_date}` : "") +
          (filters.status ? `&status=${filters.status}` : "") +
          (filters.type ? `&type=${filters.type}` : ""),
      );
      setOffers(response.data.offers || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalResults(response.data.total_results || 0);
    } catch (error: any) {
      console.warn("Error fetching offers:", error.message);
    } finally {
      closeLoading();
    }
  };

  const fetchResellers = async () => {
    if (!userSettings?.isAdmin() && !userSettings?.isCommercial()) return; // Only fetch if the user is an admin
    try {
      const response = await api.get(
        API_URLS.OFFERS +
          "/_values/resellers?" +
          (filters.search
            ? `&search=${encodeURIComponent(filters.search)}`
            : "") +
          (filters.commercial_id
            ? `&commercial_id=${filters.commercial_id}`
            : "") +
          (filters.begin_date ? `&begin_date=${filters.begin_date}` : "") +
          (filters.end_date ? `&end_date=${filters.end_date}` : "") +
          (filters.status ? `&status=${filters.status}` : "") +
          (filters.type ? `&type=${filters.type}` : ""),
      );
      const data: ResellerSummary[] = await response.data;
      setResellersList(data);
    } catch (error) {
      console.warn("Error fetching resellers:", error);
    }
  };

  const fetchCommercials = async () => {
    if (!userSettings?.isAdmin() && !userSettings?.isCommercial()) return; // Only fetch if the user is an admin
    try {
      const response = await api.get(
        API_URLS.OFFERS +
          "/_values/commercials?" +
          (filters.search
            ? `&search=${encodeURIComponent(filters.search)}`
            : "") +
          (filters.reseller_id ? `&reseller_id=${filters.reseller_id}` : "") +
          (filters.begin_date ? `&begin_date=${filters.begin_date}` : "") +
          (filters.end_date ? `&end_date=${filters.end_date}` : "") +
          (filters.status ? `&status=${filters.status}` : "") +
          (filters.type ? `&type=${filters.type}` : ""),
      );
      const data: ResellerSummary[] = await response.data;
      setCommercialsList(data);
    } catch (error) {
      console.warn("Error fetching commercials:", error);
    }
  };

  const buildQueryParams = (params: OffersFilterParams) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.set("search", params.search);
    if (params.reseller_id) queryParams.set("reseller_id", params.reseller_id);
    if (params.commercial_id)
      queryParams.set("commercial_id", params.commercial_id);
    if (params.page) queryParams.set("page", String(params.page));
    if (params.per_page) queryParams.set("per_page", String(params.per_page));
    if (params.sort_by) queryParams.set("sort_by", params.sort_by);
    if (params.sort_direction)
      queryParams.set("sort_direction", params.sort_direction);
    if (params.begin_date) queryParams.set("begin_date", params.begin_date);
    if (params.end_date) queryParams.set("end_date", params.end_date);
    if (params.status) queryParams.set("status", params.status);
    if (params.type) queryParams.set("type", params.type);
    return queryParams.toString();
  };

  const handleCloseModal = useCallback(() => {
    setOfferId(undefined);
    setModalScreen(null);
    history.pushState(
      history.state,
      "",
      "/offers?" + buildQueryParams(filters),
    );
    fetchOffers(); // Refresh offers after managing
  }, [setModalScreen, filters]);

  const handleManageOffer = useCallback(
    (offer: OfferProposal) => {
      setModalScreen({
        state: "view",
        title: `Ver Proposta: #${offer.id}`,
        childrenControlOverflow: true,
        children: <ManageOffer offer={offer} />,
        onClose: handleCloseModal,
        onShow: () => {
          setOfferId(String(offer.id));
          history.pushState(history.state, "", `/offers/${offer.id}`);
        },
      });
    },
    [setModalScreen, handleCloseModal],
  );

  const handleCopyOffer = useCallback(
    (offer: OfferProposal) => {
      const newOffer = {
        external_id: undefined,
        id: undefined,
        status: "draft",
        compound: offer.compound,
        client_type: "end_customer",
        client: {
          name: "",
        },
      } as OfferProposal;
      setModalScreen({
        state: "edit",
        title: `Copiar Proposta: #${offer.id}`,
        childrenControlOverflow: true,
        children: <ManageOffer offer={newOffer} />,
        onClose: handleCloseModal,
        onShow: () => {
          setOfferId(String(offer.id));
          history.pushState(history.state, "", `/offers/${offer.id}/copy`);
        },
      });
    },
    [setModalScreen, handleCloseModal],
  );

  useEffect(() => {
    history.pushState(
      history.state,
      "",
      "/offers?" + buildQueryParams(filters),
    );
    Promise.all([fetchResellers(), fetchCommercials()]);
  }, [history, userSettings, filters]);

  useEffect(() => {
    console.info("start OfferPage useEffect");
    console.info("filters:", filters);
    if (!isAuthenticated()) {
      navigate("/login", { state: { from: pathname } });
    }
    fetchOffers();
    if (offerId) {
      console.log("open offer with id:", offerId);
      fetchOffer(offerId);
    }
  }, [
    filters,
    offerId,
    history,
    location,
    pathname,
    navigate,
    isAuthenticated,
  ]);

  if (!isAuthenticated()) {
    return <div>You must be logged in to edit items.</div>;
  }

  return (
    <>
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex flex-col gap-6">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <span className="text-3xl font-bold text-gray-900 tracking-tight">
                Propostas
              </span>
              <p className="text-gray-500 mt-1">
                Gerencie propostas comerciais e acompanhe o pipeline de vendas.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-auto">
            <ListOfferItems
              offers={offers}
              filters={filters}
              setFilters={setFilters}
              totalPages={totalPages}
              totalResults={totalResults}
              resellersList={resellersList}
              commercialsList={commercialsList}
              handleManageOffer={handleManageOffer}
              handleCopyOffer={handleCopyOffer}
              userSettings={userSettings}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferPage;
